import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Github } from "lucide-react";

// Brand-accurate X & Medium logos (lucide doesn't ship these)
const XLogo = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const MediumLogo = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
  </svg>
);

const FOOTER_LINKS = [
  {
    title: "Products",
    links: [
      { label: "MCPolish", path: "/docs/mcpolish/" },
      { label: "EvalSig", path: "/docs/evalsig/" },
      { label: "Memnex", path: "/docs/memnex/" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blogs", path: "/blogs" },
      { label: "Cookbooks", path: "/cookbooks" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Overview", path: "/" },
      { label: "Portfolio", path: "/", portfolioHost: true },
    ],
  },
];

const isPortfolioHost =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("portfolio.");

// Derive the brand-site origin from the current location so it stays
// correct in local dev (portfolio.localhost:3000 → http://localhost:3000)
// and in production (portfolio.melonai.in → https://www.melonai.in).
function brandOrigin() {
  if (typeof window === "undefined") return "https://www.melonai.in";
  const { protocol, hostname, port } = window.location;
  let host = hostname.startsWith("portfolio.")
    ? hostname.slice("portfolio.".length)
    : hostname;
  if (host === "melonai.in") host = "www.melonai.in";
  return `${protocol}//${host}${port ? `:${port}` : ""}`;
}

// Reverse of brandOrigin: from the company host derive the portfolio
// subdomain origin so the footer Portfolio link crosses over correctly
// in both local dev and production.
function portfolioOrigin() {
  if (typeof window === "undefined") return "https://portfolio.melonai.in";
  const { protocol, hostname, port } = window.location;
  if (hostname.startsWith("portfolio.")) {
    return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
  }
  const bare = hostname.startsWith("www.") ? hostname.slice(4) : hostname;
  return `${protocol}//portfolio.${bare}${port ? `:${port}` : ""}`;
}

const BRAND_ORIGIN = brandOrigin();
const PORTFOLIO_ORIGIN = portfolioOrigin();

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#F5F5F0] border-t border-[#D4D4D4]">
      {/* Links + Social */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.svg" alt="Melonai" className="w-9 h-9" />
              <span className="font-pixel text-[#15803D] text-sm">MELONAI</span>
            </div>
            <p className="font-terminal text-xs text-[#0A0A0A]/40 leading-relaxed mb-5">
              We build the brains.<br />You build the body.
            </p>
            <div className="flex gap-2 items-center -ml-2">
              <a href="https://x.com/heyvikramdev" target="_blank" rel="noopener noreferrer" data-testid="footer-social-x" className="inline-flex items-center justify-center w-8 h-8 text-[#0A0A0A]/60 hover:text-[#15803D] transition-colors" aria-label="X">
                <XLogo width={16} height={16} />
              </a>
              <a href="https://www.linkedin.com/in/vikram-dev/" target="_blank" rel="noopener noreferrer" data-testid="footer-social-linkedin" className="inline-flex items-center justify-center w-8 h-8 text-[#0A0A0A]/60 hover:text-[#15803D] transition-colors" aria-label="LinkedIn">
                <Linkedin size={16} />
              </a>
              <a href="https://medium.com/@easyml" target="_blank" rel="noopener noreferrer" data-testid="footer-social-medium" className="inline-flex items-center justify-center w-8 h-8 text-[#0A0A0A]/60 hover:text-[#15803D] transition-colors" aria-label="Medium">
                <MediumLogo width={18} height={18} />
              </a>
              <a href="https://github.com/vtensor" target="_blank" rel="noopener noreferrer" data-testid="footer-social-github" className="inline-flex items-center justify-center w-8 h-8 text-[#0A0A0A]/60 hover:text-[#15803D] transition-colors" aria-label="GitHub">
                <Github size={16} />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {FOOTER_LINKS.map((col) => (
            <div key={col.title}>
              <p className="font-arcade text-[#15803D] text-base uppercase tracking-wider mb-4">
                {col.title}
              </p>
              <ul className="space-y-3">
                {col.links.map((link) => {
                  const className =
                    "font-terminal text-xs text-[#0A0A0A]/40 hover:text-[#15803D] transition-colors duration-150";
                  // /docs/* paths are real static files (mkdocs output), not SPA routes.
                  // Always render as raw <a> so the browser navigates instead of letting
                  // React Router intercept and fall through to NotFound.
                  const isStaticDoc = link.path.startsWith("/docs/");
                  // Portfolio link must always cross over to the portfolio
                  // subdomain (portfolio.melonai.in), regardless of which
                  // host the footer is being rendered on.
                  if (link.portfolioHost) {
                    return (
                      <li key={link.label}>
                        <a
                          href={`${PORTFOLIO_ORIGIN}${link.path}`}
                          className={className}
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  }
                  if (isPortfolioHost || isStaticDoc) {
                    return (
                      <li key={link.label}>
                        <a
                          href={isPortfolioHost ? `${BRAND_ORIGIN}${link.path}` : link.path}
                          className={className}
                        >
                          {link.label}
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={link.label}>
                      <Link to={link.path} className={className}>
                        {link.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#E5E5E5] pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="font-terminal text-xs text-[#0A0A0A]/30">
            &copy; {new Date().getFullYear()} Melonai. All rights reserved.
          </p>
          <p className="font-terminal text-xs text-[#0A0A0A]/20">
            [SYS:ONLINE] - v2.0.1
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
